import { useLiveChatCRUDActions } from '@mm/state/actions/LiveChatCRUDActions';
import { ContactMessage } from '@mm/state/model/ContactMessageModel';
import { LivechatConversationModel } from '@mm/state/model/LivechatConversationModel';
import {
  ConversationDirection,
  LivechatMessageModel,
  MessageKind,
} from '@mm/state/model/LivechatMessageModel';
import { mergeDescSortedArrays } from '@mm/utils';
import { maxBy, uniqBy } from 'lodash';
import { ActionObject, Actor, assign, createMachine, send } from 'xstate';
import { pure, sendParent } from 'xstate/lib/actions';
import { parseMessageStructureAndCampaign } from './components/Messages/parseMessage';
import { RemapMessagesDataReturn } from './shared';

type FeCassandraPagination = {
  pageState: string | null;
  updatedAt: number;
};

const getAllMessages = (messages: LivechatMessageModel[]) => {
  let allReactionsId: string[] = [];
  return messages.reduce((acc: LivechatMessageModel[], filterMessage) => {
    if (filterMessage.kind === MessageKind.reaction && filterMessage.mid) {
      const relatedMessageIndex = messages.findIndex(message => {
        return (
          message.mid === filterMessage.mid &&
          message.kind !== MessageKind.reaction
        );
      });
      if (!allReactionsId.includes(filterMessage.mid)) {
        const reactions = messages.filter(message => {
          return (
            message.mid === filterMessage.mid &&
            message.kind === MessageKind.reaction
          );
        });
        const lastReactions = maxBy(reactions, 'time');
        if (messages[relatedMessageIndex] && lastReactions) {
          if (lastReactions.structure.emoji) {
            const { emoji } = lastReactions.structure;
            const emojiRepl = emoji.replace(/[{}]/g, '');
            const localEmoji = JSON.parse(`"${emojiRepl}"`);

            messages[relatedMessageIndex] = {
              ...messages[relatedMessageIndex],
              structure: {
                ...messages[relatedMessageIndex].structure,
                emoji: localEmoji,
              },
            };
          }
        }
      }
      allReactionsId = [...allReactionsId, filterMessage.mid];
      return acc;
    }
    if (filterMessage.kind === MessageKind.delete) {
      const relatedMessageIndex = messages.findIndex(message => {
        return (
          message.mid === filterMessage.mid &&
          message.kind !== MessageKind.delete &&
          message.kind !== MessageKind.reaction
        );
      });
      if (messages[relatedMessageIndex]) {
        messages[relatedMessageIndex] = {
          ...messages[relatedMessageIndex],
          structure: {
            ...messages[relatedMessageIndex].structure,
            lcKind: 'deleted',
          },
        };
      }
      return acc;
    }
    return [...acc, filterMessage];
  }, []);
};

export type ConversationContext = {
  model: LivechatConversationModel;
  messages: LivechatMessageModel[];
  messagesPageState: FeCassandraPagination;
  messagesLatestMsgTimestamp: string;
  optimisticMessages: ContactMessage[];
  allMessages: LivechatMessageModel[];
};

export type ConversationEvents =
  | { type: 'LC_CONVERSATION_MESSAGES_FETCH_MORE' }
  | { type: 'LC_CONVERSATION_REDOWNLOAD' }
  | {
      type: 'LC_CONVERSATION_REPLACE';
      payload: LivechatConversationModel;
    }
  | {
      type: 'LC_CONVERSATION_UPDATE';
      payload: Partial<LivechatConversationModel>;
    }
  | {
      type: 'LC_CONVERSATIONS_MESSAGES_UPDATE';
      payload: {
        data: RemapMessagesDataReturn;
        isRestCall?: boolean;
      };
    }
  | { type: 'LC_SEND_OPTIMISTICALLY'; payload: ContactMessage }
  | { type: 'LC_UPDATE_OPTIMISTIC_MESSAGE'; payload: ContactMessage };

export type ConversationActor = Actor<ConversationContext, ConversationEvents>;

export const initialConversationContext = {
  model: {},
  messages: [],
  messagesLatestMsgTimestamp: '',
  messagesPageState: {
    pageState: null,
    updatedAt: 0,
  },
  optimisticMessages: [],
  archivedMessages: [],
  allMessages: [],
};

// Note: current logic makes each conversation machine independent and issuing its
// own rest calls. It seems to be working now, especially for the primary
// scenarios.
// However we might want to optimize it by doing batch requests for conversations.
// A relatively easy way to do it would be to refactoro the code so that
// the conversation machine send event for getting the data to liveChatMachine.
// There we can keep the queue of ids of conversations to get and do batched requests
// e.g. every 100ms if there are any, dispatching the results back to corresponding
// actors/machine where data would be processed next.
// The conversation machine would just need to replace the invoike with a couple fo states.
export const createConversationMachine = (
  context: ConversationContext,
  liveChatCRUDActions: ReturnType<typeof useLiveChatCRUDActions>,
) =>
  createMachine<ConversationContext, ConversationEvents>(
    {
      initial: 'init',
      context,
      predictableActionArguments: true,
      states: {
        init: {
          always: [
            {
              target: 'messagesRequest',
              cond: ctx => !!ctx.model?.remoteUserId,
            },
          ],
        },
        messagesRequest: {
          always: [
            {
              target: 'idle',
              cond: ctx =>
                ctx.allMessages.length > 0 &&
                ctx.messagesPageState.pageState === null,
            },
            {
              target: 'awaitingMessages',
              actions: sendParent((ctx, evt) => {
                const requestData = {
                  remoteUserId: ctx.model.remoteUserId,
                  pageState: ctx.messagesPageState.pageState,
                };
                return {
                  type: 'LC_CONVERSATIONS_GET_MESSAGES',
                  payload: requestData,
                };
              }),
            },
          ],
        },
        awaitingMessages: {
          after: {
            // Move out of the state, since we typically get stuck here no data is returned from BE
            5000: {
              target: 'idle',
            },
          },
          on: {
            LC_CONVERSATIONS_MESSAGES_UPDATE: {
              target: 'idle',
              actions: send((ctx, evt) => {
                return {
                  type: 'LC_CONVERSATIONS_MESSAGES_UPDATE',
                  payload: evt.payload,
                };
              }),
            },
          },
        },
        idle: {
          on: {
            LC_CONVERSATION_MESSAGES_FETCH_MORE: {
              target: 'messagesRequest',
            },
          },
        },
        refreshing: {
          invoke: {
            src: 'redownloadSpecificConversations',
            onDone: {
              target: 'idle',
              actions: pure((ctx, evt) => {
                if (evt.data) {
                  return send({
                    type: 'LC_CONVERSATION_REPLACE',
                    payload: evt.data,
                  });
                }
              }),
            },
            onError: {
              target: 'idle',
            },
          },
        },
      },
      on: {
        LC_CONVERSATION_UPDATE: {
          actions: assign((ctx, evt) => {
            return { model: { ...ctx.model, ...evt.payload } };
          }),
        },
        LC_CONVERSATION_REPLACE: {
          actions: assign((ctx, evt) => {
            return { model: evt.payload };
          }),
        },
        LC_CONVERSATIONS_MESSAGES_UPDATE: {
          // @ts-ignore Note: xState typing for pure effects seems to be wrong with latest TS versions
          actions: pure((ctx, evt) => {
            const actionsToReturn: ActionObject<
              Partial<ConversationContext>,
              ConversationEvents
            >[] = [];
            const updatedContext: Partial<ConversationContext> = {};

            const remappedData = evt.payload.data;

            if (!remappedData) {
              return;
            }

            const newMessages = remappedData.data.map(m =>
              parseMessageStructureAndCampaign(m),
            );

            if (!newMessages.length) {
              return;
            }

            const oldMessages = ctx.messages;

            const latestCurrentMessageTimestamp =
              Number(oldMessages.length && oldMessages[0].time) || 0;
            const newOldestMessageTimestamp = Number(
              newMessages[newMessages.length - 1].time,
            );

            // Deleting optimistic messages from FE after they've been processed by BE
            const incomingOptimisticMessages = newMessages.filter(
              m => !!m.structure?.clientMessageId,
            );
            if (incomingOptimisticMessages.length) {
              const newOptimisticMessages = ctx.optimisticMessages.reduce(
                (acc: ContactMessage[], cur: ContactMessage) => {
                  const isInIncoming = !!incomingOptimisticMessages.find(
                    m => m.structure.clientMessageId === cur.clientMessageId,
                  );
                  if (!isInIncoming) {
                    acc.push(cur);
                  }
                  return acc;
                },
                [],
              );
              updatedContext.optimisticMessages = newOptimisticMessages;
            }

            // Note: We need to set pageState first time only for streaming message so that it points
            // to the oldest message we have.
            // Rest calls should always reset page-state though
            if (latestCurrentMessageTimestamp === 0 || evt.payload.isRestCall) {
              updatedContext.messagesPageState = {
                pageState: remappedData.pagination.pageState,
                updatedAt: Date.now(),
              };
            }

            const areAllMessagesNew =
              newOldestMessageTimestamp > latestCurrentMessageTimestamp;

            const newerMessages = newMessages.filter(
              m => Number(m.time) > latestCurrentMessageTimestamp,
            );

            if (newerMessages.length) {
              const latestIncomingMessage = newerMessages.find(
                m => m.direction === ConversationDirection.incoming,
              );
              if (latestIncomingMessage) {
                sendParent({
                  type: 'LC_CONTACT_LAST_ACTIVE_UPDATE',
                  payload: latestIncomingMessage,
                });
              }
            }

            if (areAllMessagesNew) {
              const mergedMessages = [...newMessages, ...oldMessages];
              updatedContext.messages = mergedMessages;
              updatedContext.allMessages = getAllMessages(mergedMessages);

              // If we are in this situation and pageState is defined, we might have missing
              // messages between the initial load and the time we started geting streaming ones.
              // We need to keep fetching messages till we get all the one that we might have missed.
              updatedContext.messagesPageState = {
                pageState: remappedData.pagination.pageState,
                updatedAt: Date.now(),
              };
              if (remappedData.pagination.pageState) {
                actionsToReturn.push(
                  send({ type: 'LC_CONVERSATION_MESSAGES_FETCH_MORE' }),
                );
              }
            } else {
              // Note: we might have situations when we are getting messages in between of
              // what we already have. E.g. when initial rest call happened long time ago,
              // and streaming messages got us new that are far away.
              // oldMessages array is potentially much larger, while the new one is always limited
              // by pageSize (currently 20).
              // And old messages are supposed to be sorted by time already.
              // So I'm using Merge Sorting of two arrays here spead up the search
              // that happens for each new message, with the cycle repeated 10 times per second.

              const mergedMessages = uniqBy(
                mergeDescSortedArrays(
                  oldMessages,
                  newMessages,
                  (a, b) => Number(a['time']) > Number(b['time']),
                ),
                e => e.time,
              );

              updatedContext.messages = mergedMessages;
              updatedContext.allMessages = getAllMessages(mergedMessages);
            }

            updatedContext.messagesLatestMsgTimestamp =
              updatedContext.messages[0]?.time ?? '';
            actionsToReturn.push(assign(updatedContext));

            return actionsToReturn;
          }),
        },
        LC_SEND_OPTIMISTICALLY: {
          actions: assign((ctx, evt) => {
            return {
              optimisticMessages: [...ctx.optimisticMessages, evt.payload],
            };
          }),
        },
        LC_CONVERSATION_REDOWNLOAD: {
          target: '.refreshing',
          internal: false,
        },
        LC_UPDATE_OPTIMISTIC_MESSAGE: {
          actions: pure((ctx, evt) => {
            const { clientMessageId } = evt.payload;

            const index = ctx.optimisticMessages.findIndex(
              msg => msg.clientMessageId === clientMessageId,
            );
            if (index > -1) {
              return assign({
                optimisticMessages: [
                  ...ctx.optimisticMessages.slice(0, index),
                  evt.payload,
                  ...ctx.optimisticMessages.slice(index + 1),
                ],
              });
            }
          }),
        },
      },
    },
    {
      services: {
        redownloadSpecificConversations: async ctx => {
          const res = await liveChatCRUDActions.redownloadSpecificConversations(
            [ctx.model.remoteUserId],
            'main',
          );
          return res?.[0];
        },
      },
    },
  );
